import type { ServerError } from '../models';
import type { SnackMessage } from '../models/snackbar';

export const ERROR_MSG_INVALID_CODE = 'Retype your code or request a new one.';

// Friendly copy for known 403 reasons, keyed by the backend error `code`. Used only when the
// server sends a code but no usable details/message (e.g. message === code), so we surface a
// specific reason instead of the generic forbidden fallback.
const FORBIDDEN_REASON_BY_CODE: Record<string, string> = {
    promotion_is_not_available_in_your_region: "This promotion isn't available in your region.",
    user_not_kyc_verified: 'Please finish verifying your identity to continue.',
    employment_trading_blocked: 'Trading is paused while we review your employment details.',
    trading_blocked: 'Trading is currently restricted on your account.',
    user_deactivated: 'Your account is deactivated. Reactivate it to continue.',
};

const ERROR_MESSAGE_BY_CODE: Record<string, string> = {
    trading_limit_exceeded:
        'This order would exceed your trading limit. Reduce the order size or review your limits in Settings.',
};

// The server's `message` is a headline and `details` is the sentence that tells the user what to
// do. Returns the message only when it carries real copy — some services echo the code back, or
// send the internal_server_error placeholder, and neither is worth showing.
const getUsableMessage = (srvErr: ServerError): string | undefined => {
    const apiError = srvErr.error;
    if (
        apiError?.message &&
        apiError.message !== '' &&
        apiError.message !== 'internal_server_error' &&
        apiError.message !== apiError.code
    ) {
        return apiError.message;
    }
    return undefined;
};

const getUsableDetails = (srvErr: ServerError): string | undefined => {
    const details = srvErr.error?.details;
    return details && details.trim() !== '' ? details : undefined;
};

const getErrorMessage = (srvErr: ServerError): string => {
    const apiError = srvErr.error;
    const mappedMessage = ERROR_MESSAGE_BY_CODE[apiError?.code ?? ''];
    if (mappedMessage) {
        return mappedMessage;
    }
    const details = getUsableDetails(srvErr);
    if (details) {
        return details;
    }
    const message = getUsableMessage(srvErr);
    if (message) {
        return message;
    }
    // All the messages below are intentionally vague, because these are the fallback messages
    // They will only be used if we don't have specific messages for the error codes
    if (srvErr.maintenance) {
        return 'Kalshi is under maintenance.';
    }
    if (srvErr.unavailable) {
        return 'Service unavailable, please try again later.';
    }
    if (srvErr.notFound) {
        return 'The information you are trying to access does not exist.';
    }
    if (srvErr.invalid) {
        return 'Invalid input(s), please review the information provided.';
    }
    if (srvErr.forbidden) {
        const code = apiError?.code;
        if (code && FORBIDDEN_REASON_BY_CODE[code]) {
            return FORBIDDEN_REASON_BY_CODE[code];
        }
        return "You don't have permission to perform this operation.";
    }
    if (srvErr.conflict) {
        // Conflict is used when you violate one of the business rule like submitting orders for closed markets and so on
        return "We couldn't accept this operation.";
    }
    if (srvErr.tooManyRequests) {
        return 'This user issued too many requests.';
    }
    // srvErr.internal or anything not specified
    return 'Something went wrong, please try again later.';
};

export interface ErrorCopy {
    title: string;
    description?: string;
}

/**
 * Two-part copy for surfaces that can render a headline and a body — the shape the backend
 * actually sends. `getErrorMessage` has to collapse `{message, details}` into one string and
 * returns `details`, which is the actionable half but drops the statement of what went wrong;
 * a toast reading only "Contact support for help with your account." never says what happened.
 */
const getErrorCopy = (srvErr: ServerError): ErrorCopy => {
    const mappedMessage = ERROR_MESSAGE_BY_CODE[srvErr.error?.code ?? ''];
    if (mappedMessage) {
        return { title: mappedMessage };
    }

    const message = getUsableMessage(srvErr);
    const details = getUsableDetails(srvErr);

    if (message) {
        return { title: message, ...(details && details !== message && { description: details }) };
    }
    // No usable headline, so whatever copy exists has to carry the toast on its own.
    return { title: getErrorMessage(srvErr) };
};

/**
 * The `text`/`description` pair for a server error, to spread into pushMessage. For call sites that
 * build their own toast rather than going through handleSnackbarError — they keep their own
 * variant, action and fallback, and stop collapsing {message, details} into a single line.
 *
 * Lives here rather than beside pushMessage so that the many tests which mock the snackbar store
 * with a partial factory don't have to grow an entry for it.
 */
const serverErrorSnack = (srvErr: ServerError): Pick<SnackMessage, 'text' | 'description'> => {
    const { title, description } = getErrorCopy(srvErr);
    return { text: title, description };
};

export { getErrorMessage, getErrorCopy, serverErrorSnack };
